/**
 * The opener a surface that hosts the records panel gives its conversation (`entity-opener.ts`): a record whose type
 * has a labelled side panel (Spec 76 registry, contributed by core and each module) opens there as `?panel=<type>:<id>`,
 * when the person holds the policy its screen requires; a member's thread opens in the conversation's own place.
 */
import { CONVERSATION_ENTITY } from '@confluent/shared/lib/conversation-ui/entity-refs';
import type React from 'react';
import { evaluateClientPolicy } from '../../../auth/policies.js';
import { useAuthStore } from '../../../store/index.js';
import { getPanelType } from '../../context-panel/registry.js';
import { useContextPanel } from '../../context-panel/url-state.js';
import { useOpenConversation } from '../open-conversation-context.js';
import { type EntityOpener, EntityOpenerContext, type EntityPointer } from './entity-opener.js';

export const EntityOpenerProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { openPanel } = useContextPanel();
  const openConversation = useOpenConversation();
  const permissions = useAuthStore((s) => s.user?.permissions ?? []);
  const organizationPlan = useAuthStore((s) => s.user?.organizationPlan ?? null);

  const labelOf = (entity: string): string | null => {
    if (entity === CONVERSATION_ENTITY) return openConversation ? 'Thread' : null;
    const definition = getPanelType(entity);
    if (!definition?.label) return null;
    if (definition.policy && !evaluateClientPolicy(definition.policy, permissions, organizationPlan)) return null;
    return definition.label;
  };

  const opener: EntityOpener = {
    labelOf,
    canOpen: (ref: EntityPointer) => labelOf(ref.entity) !== null,
    open: (ref: EntityPointer) => {
      if (labelOf(ref.entity) === null) return;
      if (ref.entity === CONVERSATION_ENTITY) openConversation?.(ref.id);
      else openPanel({ type: ref.entity, id: ref.id });
    },
  };

  return <EntityOpenerContext value={opener}>{children}</EntityOpenerContext>;
};
